import BenzAMRRecorder from "benz-amr-recorder"

export const AudioEncoderAndroidType = { AAC: "aac" }
export const AudioSourceAndroidType = { MIC: "mic" }
export const AVEncoderAudioQualityIOSType = { high: "high" }
export const AVEncodingOption = { aac: "aac" }

export type PlayBackType = {
  isFinished?: boolean
  currentPosition: number
  duration: number
}

export type RecordBackType = {
  currentPosition: number
}

export type AudioSet = Record<string, unknown>

type PlayerBackend = "html" | "amr" | null

function formatMmss(sec: number): string {
  const minutes = Math.floor(sec / 60)
  const seconds = sec % 60
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
}

function isAmrSource(uri: string) {
  return /\.amr(\?|#|$)/i.test(uri)
}

export default class AudioRecorderPlayer {
  private subscriptionDurationSec = 0.1
  private audio: HTMLAudioElement | null = null
  private amr: BenzAMRRecorder | null = null
  private backend: PlayerBackend = null
  private playbackTimer: ReturnType<typeof setInterval> | null = null
  private recordTimer: ReturnType<typeof setInterval> | null = null
  private playbackListener: ((e: PlayBackType) => void) | null = null
  private recordListener: ((e: RecordBackType) => void) | null = null
  private recordStartAt = 0
  private mediaRecorder: MediaRecorder | null = null
  private recordStream: MediaStream | null = null
  private recordChunks: Blob[] = []
  private recordBlobUrl = ""
  private isPaused = false
  private onTimeUpdate: (() => void) | null = null
  private onEnded: (() => void) | null = null

  setSubscriptionDuration = (seconds: number) => {
    this.subscriptionDurationSec = seconds
  }

  mmss = (sec: number) => formatMmss(sec)

  private clearPlaybackTimer() {
    if (this.playbackTimer) {
      clearInterval(this.playbackTimer)
      this.playbackTimer = null
    }
  }

  private clearRecordTimer() {
    if (this.recordTimer) {
      clearInterval(this.recordTimer)
      this.recordTimer = null
    }
  }

  private stopRecordStream() {
    this.recordStream?.getTracks().forEach((t) => t.stop())
    this.recordStream = null
  }

  private unbindPlaybackEvents() {
    if (this.audio && this.onTimeUpdate) {
      this.audio.removeEventListener("timeupdate", this.onTimeUpdate)
    }
    if (this.audio && this.onEnded) {
      this.audio.removeEventListener("ended", this.onEnded)
    }
    this.onTimeUpdate = null
    this.onEnded = null
  }

  private bindPlaybackEvents() {
    if (!this.audio) return
    this.unbindPlaybackEvents()
    this.onTimeUpdate = () => this.emitPlayback()
    this.onEnded = () => {
      this.isPaused = false
      this.emitPlayback()
    }
    this.audio.addEventListener("timeupdate", this.onTimeUpdate)
    this.audio.addEventListener("ended", this.onEnded)
  }

  private bindAmrEvents() {
    if (!this.amr) return
    this.amr.onEnded(() => {
      this.isPaused = false
      this.emitPlayback()
    })
  }

  private emitPlayback() {
    if (!this.playbackListener) return

    if (this.backend === "amr" && this.amr) {
      const durationSec = this.amr.getDuration()
      const currentSec = this.amr.getCurrentPosition()
      const duration = Number.isFinite(durationSec) ? Math.floor(durationSec * 1000) : 0
      const currentPosition = Number.isFinite(currentSec) ? Math.floor(currentSec * 1000) : 0
      const ended =
        !this.isPaused &&
        duration > 0 &&
        !this.amr.isPlaying() &&
        !this.amr.isPaused() &&
        currentPosition >= duration - 80

      this.playbackListener({
        currentPosition,
        duration,
        isFinished: ended || (duration > 0 && currentPosition >= duration),
      })
      return
    }

    if (!this.audio) return
    const currentPosition = Math.floor(this.audio.currentTime * 1000)
    const duration = Number.isFinite(this.audio.duration)
      ? Math.floor(this.audio.duration * 1000)
      : 0
    this.playbackListener({
      currentPosition,
      duration,
      isFinished: !this.isPaused && this.audio.ended,
    })
  }

  addPlayBackListener = (callback: (e: PlayBackType) => void) => {
    this.playbackListener = callback
    this.clearPlaybackTimer()
    if (this.backend === "html") {
      this.bindPlaybackEvents()
    } else if (this.backend === "amr") {
      this.bindAmrEvents()
    }
    const ms = Math.max(this.subscriptionDurationSec * 1000, 100)
    this.playbackTimer = setInterval(() => this.emitPlayback(), ms)
    this.emitPlayback()
  }

  removePlayBackListener = () => {
    this.playbackListener = null
    this.clearPlaybackTimer()
    this.unbindPlaybackEvents()
    this.amr?.onEnded(null)
  }

  private waitForAudioReady(audio: HTMLAudioElement) {
    return new Promise<void>((resolve, reject) => {
      if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        resolve()
        return
      }

      const cleanup = () => {
        audio.removeEventListener("loadedmetadata", onReady)
        audio.removeEventListener("canplay", onReady)
        audio.removeEventListener("canplaythrough", onReady)
        audio.removeEventListener("error", onError)
      }
      const onReady = () => {
        cleanup()
        resolve()
      }
      const onError = () => {
        cleanup()
        reject(new Error(`Failed to load audio: ${audio.src}`))
      }

      audio.addEventListener("loadedmetadata", onReady, { once: true })
      audio.addEventListener("canplay", onReady, { once: true })
      audio.addEventListener("canplaythrough", onReady, { once: true })
      audio.addEventListener("error", onError, { once: true })
      audio.load()
    })
  }

  private async startHtmlPlayer(uri: string) {
    const audio = new Audio()
    audio.preload = "auto"
    audio.volume = 1
    audio.muted = false
    audio.src = uri
    this.audio = audio
    this.backend = "html"
    this.isPaused = false

    await this.waitForAudioReady(audio)
    await audio.play()
  }

  private async startAmrPlayer(uri: string) {
    const amr = new BenzAMRRecorder()
    await amr.initWithUrl(uri)
    this.amr = amr
    this.backend = "amr"
    this.isPaused = false
    this.bindAmrEvents()
    amr.play()
  }

  startPlayer = async (uri: string) => {
    await this.stopPlayer()

    if (isAmrSource(uri)) {
      await this.startAmrPlayer(uri)
      return uri
    }

    await this.startHtmlPlayer(uri)
    return uri
  }

  stopPlayer = async () => {
    this.removePlayBackListener()
    this.isPaused = false
    this.backend = null

    if (this.amr) {
      this.amr.onEnded(null)
      this.amr.stop()
      this.amr = null
    }

    if (this.audio) {
      this.audio.pause()
      this.audio.src = ""
      this.audio = null
    }
  }

  pausePlayer = async () => {
    if (!this.audio && !this.amr) return
    this.isPaused = true
    if (this.backend === "amr") {
      this.amr?.pause()
    } else {
      this.audio?.pause()
    }
  }

  resumePlayer = async () => {
    if (!this.audio && !this.amr) return
    this.isPaused = false
    if (this.backend === "amr") {
      this.amr?.resume()
    } else {
      await this.audio?.play()
    }
  }

  seekToPlayer = async (timeMs: number) => {
    if (this.backend === "amr" && this.amr) {
      this.amr.setPosition(timeMs / 1000)
    } else if (this.audio) {
      this.audio.currentTime = timeMs / 1000
    }
    this.emitPlayback()
  }

  /** 浏览器 MediaRecorder 录音 */
  startRecorder = async (path?: string) => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      throw new Error("当前浏览器不支持录音")
    }

    await this.stopRecorder()

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    this.recordStream = stream
    this.recordChunks = []

    const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : ""

    this.mediaRecorder = mimeType
      ? new MediaRecorder(stream, { mimeType })
      : new MediaRecorder(stream)

    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.recordChunks.push(e.data)
    }

    this.mediaRecorder.start(200)
    this.recordStartAt = Date.now()
    this.recordBlobUrl = ""

    return path ?? "recording.webm"
  }

  stopRecorder = async () => {
    this.removeRecordBackListener()

    if (!this.mediaRecorder || this.mediaRecorder.state === "inactive") {
      return this.recordBlobUrl || "recording.webm"
    }

    return new Promise<string>((resolve, reject) => {
      const recorder = this.mediaRecorder!
      recorder.onstop = () => {
        const blob = new Blob(this.recordChunks, {
          type: recorder.mimeType || "audio/webm",
        })
        if (this.recordBlobUrl) URL.revokeObjectURL(this.recordBlobUrl)
        this.recordBlobUrl = URL.createObjectURL(blob)
        this.mediaRecorder = null
        this.recordChunks = []
        this.stopRecordStream()
        resolve(this.recordBlobUrl)
      }
      recorder.onerror = () => {
        this.stopRecordStream()
        reject(new Error("录音失败"))
      }
      try {
        recorder.stop()
      } catch (e) {
        this.stopRecordStream()
        reject(e instanceof Error ? e : new Error("录音停止失败"))
      }
    })
  }

  addRecordBackListener = (callback: (e: RecordBackType) => void) => {
    this.recordListener = callback
    this.clearRecordTimer()
    this.recordStartAt = Date.now()
    const ms = Math.max(this.subscriptionDurationSec * 1000, 100)
    this.recordTimer = setInterval(() => {
      this.recordListener?.({ currentPosition: Date.now() - this.recordStartAt })
    }, ms)
  }

  removeRecordBackListener = () => {
    this.recordListener = null
    this.clearRecordTimer()
  }
}
